import React, { useState } from 'react';
import { Bot } from 'lucide-react';
import { generateFieldContent, GenerateFieldContentRequest } from '../services/api';
import './AIFieldGenerator.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface AIFieldGeneratorProps {
    fieldType: GenerateFieldContentRequest['field_type'];
    projectId: string;
    currentValue?: string;
    context?: Record<string, any>;
    onAccept: (content: string) => void;
    label: string;
    placeholder?: string;
}

const AIFieldGenerator: React.FC<AIFieldGeneratorProps> = ({
    fieldType,
    projectId,
    currentValue,
    context,
    onAccept,
    label,
    placeholder
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedContent, setGeneratedContent] = useState<string>('');
    const [userPrompt, setUserPrompt] = useState('');
    const [error, setError] = useState('');

    const getDefaultPrompt = (): string => {
        const prompts: Record<typeof fieldType, string> = {
            'value_proposition': 'Generate 3 compelling value propositions',
            'target_segment': 'Describe the ideal target audience for this campaign',
            'quiz_purpose': 'Explain what this quiz should assess or qualify',
            'testimonials': 'Generate 3 example testimonials (to be replaced with real ones)',
            'faq_questions': 'List 5 frequently asked questions',
            'services': 'Identify 3-4 core services or practice areas'
        };
        return prompts[fieldType];
    };

    const handleGenerate = async () => {
        if (!projectId) {
            setError('Project ID is required');
            return;
        }

        setIsGenerating(true);
        setError('');

        try {
            const result = await generateFieldContent({
                field_type: fieldType,
                project_id: projectId,
                user_prompt: userPrompt || getDefaultPrompt(),
                context: {
                    ...context,
                    current_value: currentValue
                }
            });

            // Strip AI preamble like "Sure! Here are...", "Here's the...", "Certainly! Here are..."
            const stripped = (result.content || '').replace(
                /^(?:Sure[!.,]?\s*|Certainly[!.,]?\s*|Absolutely[!.,]?\s*|Of course[!.,]?\s*)?(?:Here(?:'s| are| is)\s[\s\S]*?(?:proposition|benefits|testimonials|questions|services|segment|audience|content)[^:\n]*[:\n]\s*)/i,
                ''
            ).trim();
            setGeneratedContent(stripped || result.content);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to generate content'));
        } finally {
            setIsGenerating(false);
        }
    };

    const handleAccept = () => {
        if (generatedContent) {
            onAccept(generatedContent);
            setIsOpen(false);
            setGeneratedContent('');
            setUserPrompt('');
        }
    };

    const handleClose = () => {
        setIsOpen(false);
        setGeneratedContent('');
        setUserPrompt('');
        setError('');
    };

    return (
        <div className="ai-field-generator">
            <button
                type="button"
                onClick={() => setIsOpen(true)}
                className="ai-generate-btn"
                title={`Generate ${label} with AI`}
            >
                ✨ Generate with AI
            </button>

            {isOpen && (
                <div className="ai-modal-overlay" onClick={handleClose}>
                    <div className="ai-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="ai-modal-header">
                            <h3>Generate {label}</h3>
                            <button className="ai-modal-close" onClick={handleClose}>✕</button>
                        </div>

                        <div className="ai-modal-body">
                            <div className="ai-prompt-section">
                                <label>What would you like to generate?</label>
                                <textarea
                                    value={userPrompt}
                                    onChange={(e) => setUserPrompt(e.target.value)}
                                    placeholder={placeholder || getDefaultPrompt()}
                                    rows={3}
                                    className="ai-prompt-input"
                                />
                            </div>

                            <div className="ai-actions">
                                <button
                                    onClick={handleGenerate}
                                    disabled={isGenerating}
                                    className="ai-btn-generate"
                                >
                                    {isGenerating ? '⏳ Generating...' : <><Bot size={14} style={{marginRight: 4, verticalAlign: 'middle'}} /> Generate</>}
                                </button>
                            </div>

                            {error && (
                                <div className="ai-error">{error}</div>
                            )}

                            {generatedContent && (
                                <div className="ai-result-section">
                                    <label>Generated Content:</label>
                                    <div className="ai-result-content">
                                        {generatedContent}
                                    </div>
                                    <div className="ai-result-actions">
                                        <button
                                            onClick={handleAccept}
                                            className="ai-btn-accept"
                                        >
                                            ✓ Use This
                                        </button>
                                        <button
                                            onClick={() => {
                                                setGeneratedContent('');
                                                setUserPrompt('Try with different wording or focus');
                                            }}
                                            className="ai-btn-regenerate"
                                        >
                                            🔄 Regenerate
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AIFieldGenerator;
